import type { jsPDF } from 'jspdf';

// ---------- 2026-10-28 (Owner: 「英文报告改真文字pdf……不影响质量的话怎么小怎么来」): English documents as real text ----------
//
// The renderer used to print every page as ONE picture (./pdf.ts): Chinese characters and the DEMO watermark
// print exactly as on screen, but a report of 85 photos came to 24 MB on the iPhone (peers: 2–5 MB). An
// English document (root data-text-layer="true": the English client report, the English agreement, an
// addendum) is now drawn in three layers instead:
//   1. the page's graphics — lines, boxes, chips, the DEMO watermark, signatures — as PDF shapes, from the
//      laid-out document's own boxes (a picture of them was 50 KB a page for lines on white);
//   2. every photo once, cropped to what the page shows and at the resolution the page picture gave it
//      (2 device pixels per layout pixel), with its rounded corners and border;
//   3. every word as real PDF text in Helvetica, at the place, size, weight and colour the laid-out document
//      gives it — sharper than before, searchable and selectable (it replaces the invisible text layer).
// A word with a character Helvetica cannot encode (an arrow, a check box, a Chinese name) is drawn as a small
// picture of itself at its place, so nothing is ever garbled or lost. Chinese documents keep the page picture.

// Characters the PDF's standard Helvetica encodes (WinAnsi): printable ASCII, Latin-1, and the Windows-1252
// punctuation jsPDF maps.
const WIN_ANSI = /^[ -~ -ÿ–—‘’‚“”„†‡•…‰‹›€™ŒœŠšŸŽžƒˆ˜]*$/;

export function pdfEncodable(text: string): boolean {
  return WIN_ANSI.test(text);
}

// CSS text-transform, applied to one word the way the browser painted it.
export function transformedWord(text: string, transform: string): string {
  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  if (transform === 'capitalize') return text.charAt(0).toUpperCase() + text.slice(1);
  return text;
}

// The spacing that makes a word drawn in Helvetica as wide as the browser laid it out (letter-spacing on the
// labels included). Kept small: a word never visibly stretches.
export function charSpaceFor(targetWidth: number, naturalWidth: number, length: number, fontSize: number): number {
  if (length < 2 || !Number.isFinite(targetWidth) || !Number.isFinite(naturalWidth)) return 0;
  const space = (targetWidth - naturalWidth) / (length - 1);
  const limit = fontSize * 0.15;
  return Math.max(-limit, Math.min(limit, space));
}

// Where a word goes and how wide it is drawn, from its Range rect and its own measured advance (letter-spacing
// after every letter included, as the browser lays it out). WebKit snaps a word's Range rect out to whole
// pixels (iPhone, 2026-10-28: 「York」 30.1 px came back 32 px, every gap 3.0 or 4.0), so a word stretched to its
// rect ate into the space after it (「Yearbuilt」, 「PAIDINSPECTION」): the word is centred in its rect instead,
// within half a pixel of where it was painted. The spacing after its last letter is not ink: it is left out.
export function fitWordToRect(left: number, rectWidth: number, advance: number, letterSpacing: number): { x: number; w: number } {
  if (!(advance > 0) || !Number.isFinite(advance)) return { x: left, w: rectWidth };
  const trailing = letterSpacing > 0 && Number.isFinite(letterSpacing) ? letterSpacing : 0;
  return { x: left + (rectWidth - advance) / 2, w: Math.max(advance - trailing, advance / 2) };
}

export interface FittedPicture {
  // the part of the source image that shows (source pixels)
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  // where it lands inside the element's box (layout px)
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

// CSS object-fit (centred, the report's only object-position) for an image of natural size w × h in a box W × H.
export function fitPicture(w: number, h: number, boxW: number, boxH: number, fit: string): FittedPicture {
  if (w <= 0 || h <= 0 || boxW <= 0 || boxH <= 0) return { sx: 0, sy: 0, sw: Math.max(1, w), sh: Math.max(1, h), dx: 0, dy: 0, dw: boxW, dh: boxH };
  if (fit === 'cover') {
    const scale = Math.max(boxW / w, boxH / h);
    const sw = boxW / scale;
    const sh = boxH / scale;
    return { sx: (w - sw) / 2, sy: (h - sh) / 2, sw, sh, dx: 0, dy: 0, dw: boxW, dh: boxH };
  }
  if (fit === 'contain' || fit === 'scale-down') {
    let scale = Math.min(boxW / w, boxH / h);
    if (fit === 'scale-down') scale = Math.min(scale, 1);
    const dw = w * scale;
    const dh = h * scale;
    return { sx: 0, sy: 0, sw: w, sh: h, dx: (boxW - dw) / 2, dy: (boxH - dh) / 2, dw, dh };
  }
  // fill (the default)
  return { sx: 0, sy: 0, sw: w, sh: h, dx: 0, dy: 0, dw: boxW, dh: boxH };
}

export function parseCssColor(value: string): { rgb: [number, number, number]; alpha: number } | null {
  const match = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)/.exec(value);
  if (!match) return null;
  const alphaText = match[4];
  const alpha = alphaText === undefined ? 1 : alphaText.endsWith('%') ? parseFloat(alphaText) / 100 : parseFloat(alphaText);
  return { rgb: [Math.round(+match[1]), Math.round(+match[2]), Math.round(+match[3])], alpha };
}

// ---------- in the browser ----------

export interface TextRun {
  text: string;
  // the word's box on the laid-out document (layout px, document top-left)
  x: number;
  y: number;
  w: number;
  h: number;
  sizePx: number;
  bold: boolean;
  italic: boolean;
  rgb: [number, number, number];
  // where the baseline sits in the box, as a fraction of its height
  ascent: number;
  // the CSS font, for a word drawn as a picture
  font: string;
}

interface RunStyle {
  sizePx: number;
  bold: boolean;
  italic: boolean;
  rgb: [number, number, number];
  transform: string;
  letterSpacing: number;
  ascent: number;
  font: string;
}

let measureContext: CanvasRenderingContext2D | null = null;
const ascentCache = new Map<string, number>();

// The baseline's place in a text box: the font's ascent over its ascent + descent (the box a text range reports).
function ascentOf(font: string): number {
  const known = ascentCache.get(font);
  if (known !== undefined) return known;
  if (!measureContext) measureContext = document.createElement('canvas').getContext('2d');
  let ratio = 0.78;
  if (measureContext) {
    measureContext.font = font;
    const m = measureContext.measureText('Hg');
    const a = m.fontBoundingBoxAscent;
    const d = m.fontBoundingBoxDescent;
    if (Number.isFinite(a) && Number.isFinite(d) && a > 0 && a + d > 0) ratio = a / (a + d);
  }
  ascentCache.set(font, ratio);
  return ratio;
}

function runStyleOf(el: Element): RunStyle | null {
  const cs = getComputedStyle(el);
  if (cs.visibility !== 'visible') return null;
  const color = parseCssColor(cs.color);
  if (!color || color.alpha < 0.05) return null;
  const weight = parseInt(cs.fontWeight, 10);
  const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return {
    sizePx: parseFloat(cs.fontSize),
    bold: cs.fontWeight === 'bold' || (Number.isFinite(weight) && weight >= 600),
    italic: cs.fontStyle === 'italic' || cs.fontStyle.startsWith('oblique'),
    rgb: color.rgb,
    transform: cs.textTransform,
    letterSpacing: parseFloat(cs.letterSpacing) || 0,
    ascent: ascentOf(font),
    font
  };
}

// Every visible word of the laid-out document, where and how the browser painted it. A word the browser broke
// over two lines (a long word allowed to wrap) is taken letter by letter.
export function collectTextRuns(content: HTMLElement): TextRun[] {
  const origin = content.getBoundingClientRect();
  const runs: TextRun[] = [];
  const styles = new Map<Element, RunStyle | null>();
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const push = (text: string, rect: DOMRect, style: RunStyle, fit: { x: number; w: number } = { x: rect.left - origin.left, w: rect.width }) => {
    if (rect.width <= 0 || rect.height <= 0) return;
    runs.push({ text, x: fit.x, y: rect.top - origin.top, w: fit.w, h: rect.height, sizePx: style.sizePx, bold: style.bold, italic: style.italic, rgb: style.rgb, ascent: style.ascent, font: style.font });
  };
  // A word's own advance, measured once per look and word in a span styled like it (see fitWordToRect).
  const measurer = document.createElement('span');
  measurer.style.cssText = 'position:absolute;left:0;top:0;visibility:hidden;white-space:pre;pointer-events:none';
  (content.parentElement ?? document.body).appendChild(measurer);
  const advances = new Map<string, number>();
  const advanceOf = (word: string, style: RunStyle): number => {
    const key = `${style.font}|${style.letterSpacing}|${style.transform}|${word}`;
    const known = advances.get(key);
    if (known !== undefined) return known;
    measurer.style.font = style.font;
    measurer.style.letterSpacing = `${style.letterSpacing}px`;
    measurer.style.textTransform = style.transform;
    measurer.textContent = word;
    const advance = measurer.getBoundingClientRect().width;
    advances.set(key, advance);
    return advance;
  };
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (!parent || parent.closest('svg')) continue;
    const value = node.textContent ?? '';
    if (value.trim().length === 0) continue;
    let style = styles.get(parent);
    if (style === undefined) {
      style = runStyleOf(parent);
      styles.set(parent, style);
    }
    if (!style) continue;
    const pattern = /\S+/g;
    for (let match = pattern.exec(value); match; match = pattern.exec(value)) {
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      const rects = range.getClientRects();
      if (rects.length === 1) {
        const rect = rects[0];
        push(transformedWord(match[0], style.transform), rect, style, fitWordToRect(rect.left - origin.left, rect.width, advanceOf(match[0], style), style.letterSpacing));
        continue;
      }
      for (let i = 0; i < match[0].length; i += 1) {
        range.setStart(node, match.index + i);
        range.setEnd(node, match.index + i + 1);
        const rect = range.getBoundingClientRect();
        push(transformedWord(match[0][i], style.transform === 'capitalize' && i > 0 ? 'none' : style.transform), rect, style);
      }
    }
  }
  measurer.remove();
  return runs;
}

// ---------- the page's graphics, drawn as PDF shapes ----------
// The report and agreement stylesheets use plain boxes only: background colours (some translucent, so the
// DEMO watermark shows through), solid or dotted borders, rounded corners. Each element's box is drawn as
// what it is — a filled rectangle, a stroked outline, a strip for a one-sided border — instead of a picture.

export interface BoxSide {
  width: number;
  style: string;
  rgb: [number, number, number];
  alpha: number;
}

export interface PaintBox {
  x: number;
  y: number;
  w: number;
  h: number;
  fill: { rgb: [number, number, number]; alpha: number } | null;
  radius: number;
  // top, right, bottom, left
  sides: [BoxSide | null, BoxSide | null, BoxSide | null, BoxSide | null];
}

const SIDES = ['top', 'right', 'bottom', 'left'] as const;

function sideOf(cs: CSSStyleDeclaration, side: (typeof SIDES)[number]): BoxSide | null {
  const width = parseFloat(cs.getPropertyValue(`border-${side}-width`));
  const style = cs.getPropertyValue(`border-${side}-style`);
  if (!(width > 0) || style === 'none' || style === 'hidden') return null;
  const color = parseCssColor(cs.getPropertyValue(`border-${side}-color`));
  if (!color || color.alpha < 0.02) return null;
  return { width, style, rgb: color.rgb, alpha: color.alpha };
}

// Every visible box under the root with something to paint, in document order (so later boxes paint over
// earlier ones, as in the browser). The page itself is white: a white fill paints nothing.
export function collectBoxes(content: HTMLElement): PaintBox[] {
  const origin = content.getBoundingClientRect();
  const boxes: PaintBox[] = [];
  for (const el of Array.from(content.querySelectorAll<HTMLElement>('*'))) {
    if (el instanceof HTMLImageElement || el.closest('svg')) continue;
    const cs = getComputedStyle(el);
    if (cs.visibility !== 'visible' || cs.display === 'none') continue;
    const background = parseCssColor(cs.backgroundColor);
    const fill = background && background.alpha >= 0.02 && !(background.alpha >= 0.99 && background.rgb.every((c) => c === 255)) ? background : null;
    const sides = SIDES.map((side) => sideOf(cs, side)) as PaintBox['sides'];
    if (!fill && sides.every((s) => s === null)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    const radiusText = cs.borderTopLeftRadius;
    const radius = radiusText.endsWith('%') ? (parseFloat(radiusText) / 100) * Math.min(rect.width, rect.height) : parseFloat(radiusText) || 0;
    boxes.push({ x: rect.left - origin.left, y: rect.top - origin.top, w: rect.width, h: rect.height, fill, radius: Math.min(radius, rect.width / 2, rect.height / 2), sides });
  }
  return boxes;
}

type GStateMaker = new (parameters: Record<string, number>) => unknown;

function withOpacity(doc: jsPDF, alpha: number, paint: () => void): void {
  if (alpha >= 0.99) {
    paint();
    return;
  }
  const GState = (doc as unknown as { GState: GStateMaker }).GState;
  doc.saveGraphicsState();
  doc.setGState(new GState({ opacity: alpha, 'stroke-opacity': alpha }));
  paint();
  doc.restoreGraphicsState();
}

function sameSide(a: BoxSide | null, b: BoxSide | null): boolean {
  return !!a && !!b && a.width === b.width && a.style === b.style && a.alpha === b.alpha && a.rgb.join() === b.rgb.join();
}

// One straight border: a filled strip for a solid one, a dotted / dashed line otherwise.
function drawSide(doc: jsPDF, side: BoxSide, x: number, y: number, w: number, h: number, horizontal: boolean): void {
  withOpacity(doc, side.alpha, () => {
    if (side.style === 'dotted' || side.style === 'dashed') {
      const thickness = horizontal ? h : w;
      doc.setDrawColor(side.rgb[0], side.rgb[1], side.rgb[2]);
      doc.setLineWidth(thickness);
      doc.setLineCap(side.style === 'dotted' ? 'round' : 'butt');
      doc.setLineDashPattern(side.style === 'dotted' ? [0.01, thickness * 2] : [thickness * 3, thickness * 2], 0);
      if (horizontal) doc.line(x, y + h / 2, x + w, y + h / 2);
      else doc.line(x + w / 2, y, x + w / 2, y + h);
      doc.setLineDashPattern([], 0);
      doc.setLineCap('butt');
      return;
    }
    doc.setFillColor(side.rgb[0], side.rgb[1], side.rgb[2]);
    doc.rect(x, y, w, h, 'F');
  });
}

// The boxes of one page, cut to it: a box running over a page break paints its part on each page (its top
// border on the first, its bottom border on the last).
export function drawBoxes(doc: jsPDF, boxes: readonly PaintBox[], page: { start: number; end: number }, origin: { x: number; y: number }, ptPerPx: number): void {
  const px = (v: number) => v * ptPerPx;
  for (const box of boxes) {
    const top = Math.max(box.y, page.start);
    const bottom = Math.min(box.y + box.h, page.end);
    if (bottom - top <= 0.01) continue;
    const cutTop = box.y < page.start - 0.5;
    const cutBottom = box.y + box.h > page.end + 0.5;
    const X = origin.x + px(box.x);
    const W = px(box.w);
    const Y = origin.y + px(top - page.start);
    const H = px(bottom - top);
    const radius = cutTop || cutBottom ? 0 : px(box.radius);
    const [t, r, b, l] = box.sides;
    const uniform = sameSide(t, r) && sameSide(t, b) && sameSide(t, l) && t !== null && t.style !== 'dotted' && t.style !== 'dashed';
    if (box.fill) {
      const fill = box.fill;
      withOpacity(doc, fill.alpha, () => {
        doc.setFillColor(fill.rgb[0], fill.rgb[1], fill.rgb[2]);
        if (radius > 0.3) doc.roundedRect(X, Y, W, H, radius, radius, 'F');
        else doc.rect(X, Y, W, H, 'F');
      });
    }
    if (uniform && t && radius > 0.3) {
      const width = px(t.width);
      withOpacity(doc, t.alpha, () => {
        doc.setDrawColor(t.rgb[0], t.rgb[1], t.rgb[2]);
        doc.setLineWidth(width);
        doc.roundedRect(X + width / 2, Y + width / 2, W - width, H - width, Math.max(0, radius - width / 2), Math.max(0, radius - width / 2), 'S');
      });
      continue;
    }
    if (t && !cutTop) drawSide(doc, t, X, Y, W, px(t.width), true);
    if (b && !cutBottom) drawSide(doc, b, X, Y + H - px(b.width), W, px(b.width), true);
    if (l) drawSide(doc, l, X, Y, px(l.width), H, false);
    if (r) drawSide(doc, r, X + W - px(r.width), Y, px(r.width), H, false);
  }
}

// Inline SVG (the signatures on an agreement) as PNG pictures at their place.
export interface SvgPicture {
  x: number;
  y: number;
  w: number;
  h: number;
  url: string;
}

export async function rasterizeSvgElements(content: HTMLElement, scale: number): Promise<SvgPicture[]> {
  const origin = content.getBoundingClientRect();
  const out: SvgPicture[] = [];
  for (const svg of Array.from(content.querySelectorAll('svg'))) {
    if (svg.parentElement?.closest('svg')) continue;
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(svg))}`;
    const img = await loadPicture(source);
    if (!img) continue;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(rect.width * scale));
    canvas.height = Math.max(1, Math.round(rect.height * scale));
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        out.push({ x: rect.left - origin.left, y: rect.top - origin.top, w: rect.width, h: rect.height, url: canvas.toDataURL('image/png') });
      } catch {
        // a picture that cannot be read back is left out of the PDF, never the whole document
      }
    }
    canvas.width = 0;
    canvas.height = 0;
  }
  return out;
}

export function drawSvgPictures(doc: jsPDF, pictures: readonly SvgPicture[], page: { start: number; end: number }, origin: { x: number; y: number }, ptPerPx: number): void {
  pictures.forEach((p, index) => {
    const mid = p.y + p.h / 2;
    if (mid < page.start || mid >= page.end) return;
    doc.addImage(p.url, 'PNG', origin.x + p.x * ptPerPx, origin.y + (p.y - page.start) * ptPerPx, p.w * ptPerPx, p.h * ptPerPx, `svg-${index}`);
  });
}

// The DEMO watermark: the root's tiled background (already a PNG, ./pdf.ts rasterizeSvgBackgrounds), each tile
// stored once in the PDF and placed over the page under everything else.
export interface Watermark {
  url: string;
  x: number;
  y: number;
  w: number;
  h: number;
  tileW: number;
  tileH: number;
}

export async function watermarkOf(root: HTMLElement | null, content: HTMLElement): Promise<Watermark | null> {
  if (!root) return null;
  const match = /url\(\s*(['"]?)(data:image\/png[^'")]*)\1\s*\)/.exec(root.style.backgroundImage);
  if (!match) return null;
  const img = await loadPicture(match[2]);
  if (!img) return null;
  const [w, h] = root.style.backgroundSize.split(/\s+/).map((part) => (part.endsWith('px') ? parseFloat(part) : Number.NaN));
  const origin = content.getBoundingClientRect();
  const rect = root.getBoundingClientRect();
  const tileW = Number.isFinite(w) && w > 0 ? w : img.naturalWidth;
  const tileH = Number.isFinite(h) && h > 0 ? h : Number.isFinite(w) && w > 0 ? (w * img.naturalHeight) / img.naturalWidth : img.naturalHeight;
  return { url: match[2], x: rect.left - origin.left, y: rect.top - origin.top, w: rect.width, h: rect.height, tileW, tileH };
}

export function drawWatermark(doc: jsPDF, mark: Watermark, page: { start: number; end: number }, origin: { x: number; y: number }, ptPerPx: number): void {
  const px = (v: number) => v * ptPerPx;
  doc.saveGraphicsState();
  doc.rect(origin.x + px(mark.x), origin.y, px(mark.w), px(page.end - page.start), null);
  doc.clip();
  doc.discardPath();
  const first = Math.floor((page.start - mark.y) / mark.tileH);
  for (let row = Math.max(0, first); mark.y + row * mark.tileH < Math.min(page.end, mark.y + mark.h); row += 1) {
    for (let col = 0; col * mark.tileW < mark.w; col += 1) {
      doc.addImage(mark.url, 'PNG', origin.x + px(mark.x + col * mark.tileW), origin.y + px(mark.y + row * mark.tileH - page.start), px(mark.tileW), px(mark.tileH), 'watermark');
    }
  }
  doc.restoreGraphicsState();
}

// The laid-out document's own typeface for real text: Helvetica where the system has it (iPhone, Mac),
// Arial (the same widths) elsewhere, so the words the browser measured are the words the PDF draws.
export const VECTOR_FONT_CSS = '[data-report-render] .rp,[data-report-render] .rp *,[data-report-render] .ag,[data-report-render] .ag *{font-family:Helvetica,Arial,"Liberation Sans",sans-serif !important}';

// The width a string is drawn at in the current font and size. getTextWidth applies Helvetica's kerning table,
// but the PDF draws the string without it: 「York」 came out 1.8 pt wider than its place and ate the space after
// it (2026-10-28, desktop and iPhone alike).
export function drawnWidth(doc: jsPDF, text: string): number {
  return (doc.getStringUnitWidth(text, { doKerning: false }) * doc.getFontSize()) / doc.internal.scaleFactor;
}

function helveticaStyle(run: Pick<TextRun, 'bold' | 'italic'>): string {
  return run.bold ? (run.italic ? 'bolditalic' : 'bold') : run.italic ? 'italic' : 'normal';
}

// A word Helvetica cannot encode, as a picture of itself in the browser's own font (cached per word and look).
const wordPictures = new Map<string, string | null>();

function wordPicture(run: TextRun, scale: number): string | null {
  const key = `${run.font}|${run.rgb.join(',')}|${run.text}`;
  if (wordPictures.has(key)) return wordPictures.get(key) ?? null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(run.w * scale));
  canvas.height = Math.max(1, Math.ceil(run.h * scale));
  const ctx = canvas.getContext('2d');
  let url: string | null = null;
  if (ctx) {
    ctx.scale(scale, scale);
    ctx.font = run.font;
    ctx.fillStyle = `rgb(${run.rgb.join(',')})`;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(run.text, 0, run.h * run.ascent);
    try {
      url = canvas.toDataURL('image/png');
    } catch {
      url = null;
    }
  }
  canvas.width = 0;
  canvas.height = 0;
  wordPictures.set(key, url);
  return url;
}

export function resetWordPictures(): void {
  wordPictures.clear();
}

// Draws one page's words: `top` is the page's top (layout px), `left` / `originY` the page's printable origin (pt).
export function drawTextRuns(doc: jsPDF, runs: readonly TextRun[], page: { start: number; end: number }, origin: { x: number; y: number }, ptPerPx: number, scale: number): void {
  let font = '';
  let size = 0;
  let colour = '';
  for (const run of runs) {
    const mid = run.y + run.h / 2;
    if (mid < page.start || mid >= page.end) continue;
    const x = origin.x + run.x * ptPerPx;
    const top = origin.y + (run.y - page.start) * ptPerPx;
    if (!pdfEncodable(run.text)) {
      const picture = wordPicture(run, scale * 2);
      if (picture) doc.addImage(picture, 'PNG', x, top, run.w * ptPerPx, run.h * ptPerPx, `w:${run.font}|${run.rgb.join(',')}|${run.text}`);
      continue;
    }
    const style = helveticaStyle(run);
    if (style !== font) {
      doc.setFont('helvetica', style);
      font = style;
    }
    const sizePt = run.sizePx * ptPerPx;
    if (sizePt !== size) {
      doc.setFontSize(sizePt);
      size = sizePt;
    }
    const rgb = run.rgb.join(',');
    if (rgb !== colour) {
      doc.setTextColor(run.rgb[0], run.rgb[1], run.rgb[2]);
      colour = rgb;
    }
    const charSpace = charSpaceFor(run.w * ptPerPx, drawnWidth(doc, run.text), run.text.length, sizePt);
    doc.text(run.text, x, top + run.h * run.ascent * ptPerPx, { baseline: 'alphabetic', charSpace });
  }
}

// One photo of the page: its element, the box it was frozen at, and its source.
export interface PhotoSlot {
  img: HTMLImageElement;
  src: string;
  top: number;
  bottom: number;
}

interface PhotoBox {
  x: number;
  y: number;
  w: number;
  h: number;
  fit: string;
  radius: number;
  border: number;
  borderRgb: [number, number, number] | null;
}

function photoBoxOf(img: HTMLImageElement, content: HTMLElement): PhotoBox {
  const origin = content.getBoundingClientRect();
  const rect = img.getBoundingClientRect();
  const cs = getComputedStyle(img);
  const radiusText = cs.borderTopLeftRadius;
  const radius = radiusText.endsWith('%') ? (parseFloat(radiusText) / 100) * Math.min(rect.width, rect.height) : parseFloat(radiusText) || 0;
  const border = parseFloat(cs.borderTopWidth) || 0;
  return {
    x: rect.left - origin.left,
    y: rect.top - origin.top,
    w: rect.width,
    h: rect.height,
    fit: cs.objectFit || 'fill',
    radius: Math.min(radius, rect.width / 2, rect.height / 2),
    border,
    borderRgb: border > 0 ? (parseCssColor(cs.borderTopColor)?.rgb ?? null) : null
  };
}

function loadPicture(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// The measured boxes of the photos, taken while the document is laid out (before they are hidden).
export function measurePhotos(slots: readonly PhotoSlot[], content: HTMLElement): Array<{ slot: PhotoSlot; box: PhotoBox }> {
  return slots.map((slot) => ({ slot, box: photoBoxOf(slot.img, content) }));
}

// One photo as it goes into the PDF (encoded once, reused wherever the same crop appears again).
export interface EncodedPhoto {
  url: string;
  alias: string;
  fit: FittedPicture;
}

async function encodePhoto(src: string, inner: { w: number; h: number }, fitMode: string, scale: number, alias: string): Promise<EncodedPhoto | null> {
  const img = await loadPicture(src);
  if (!img || img.naturalWidth <= 0) return null;
  const fit = fitPicture(img.naturalWidth, img.naturalHeight, inner.w, inner.h, fitMode);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(Math.min(fit.dw * scale, fit.sw)));
  canvas.height = Math.max(1, Math.round(Math.min(fit.dh * scale, fit.sh)));
  const ctx = canvas.getContext('2d');
  let url: string | null = null;
  if (ctx) {
    // the page is white: a transparent logo keeps its look as a JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, fit.sx, fit.sy, fit.sw, fit.sh, 0, 0, canvas.width, canvas.height);
    try {
      // a thumbnail or a logo is small: a higher quality costs next to nothing
      url = canvas.toDataURL('image/jpeg', canvas.width * canvas.height < 90_000 ? 0.9 : 0.8);
    } catch {
      url = null;
    }
  }
  canvas.width = 0;
  canvas.height = 0;
  return url ? { url, alias, fit } : null;
}

// Draws the photos that sit on this page. Each photo is cropped to what the page shows and encoded once at
// `scale` device pixels per layout pixel (never above its own pixels), JPEG 0.8 like the page pictures were.
export async function drawPhotos(
  doc: jsPDF,
  photos: ReadonlyArray<{ slot: PhotoSlot; box: PhotoBox }>,
  page: { start: number; end: number },
  origin: { x: number; y: number },
  ptPerPx: number,
  scale: number,
  encoded: Map<string, EncodedPhoto | null>
): Promise<number> {
  let drawn = 0;
  for (const { slot, box } of photos) {
    const mid = box.y + box.h / 2;
    if (mid < page.start || mid >= page.end || box.w <= 1 || box.h <= 1) continue;
    const inner = { x: box.x + box.border, y: box.y + box.border, w: Math.max(1, box.w - 2 * box.border), h: Math.max(1, box.h - 2 * box.border) };
    const key = `${slot.src}|${Math.round(inner.w)}x${Math.round(inner.h)}|${box.fit}`;
    let picture = encoded.get(key);
    if (picture === undefined) {
      picture = await encodePhoto(slot.src, inner, box.fit, scale, `photo-${encoded.size}`);
      encoded.set(key, picture);
    }
    if (!picture) continue;
    const fit = picture.fit;
    const px = (v: number) => v * ptPerPx;
    const X = origin.x + px(box.x);
    const Y = origin.y + px(box.y - page.start);
    const W = px(box.w);
    const H = px(box.h);
    const radius = px(box.radius);
    doc.saveGraphicsState();
    if (radius > 0.5) {
      doc.roundedRect(X, Y, W, H, radius, radius, null);
      doc.clip();
      doc.discardPath();
    }
    doc.addImage(picture.url, 'JPEG', origin.x + px(inner.x + fit.dx), origin.y + px(inner.y + fit.dy - page.start), px(fit.dw), px(fit.dh), picture.alias);
    doc.restoreGraphicsState();
    if (box.border > 0 && box.borderRgb) {
      const b = px(box.border);
      doc.setDrawColor(box.borderRgb[0], box.borderRgb[1], box.borderRgb[2]);
      doc.setLineWidth(b);
      if (radius > 0.5) doc.roundedRect(X + b / 2, Y + b / 2, W - b, H - b, Math.max(0, radius - b / 2), Math.max(0, radius - b / 2), 'S');
      else doc.rect(X + b / 2, Y + b / 2, W - b, H - b, 'S');
    }
    drawn += 1;
  }
  return drawn;
}

// The running header / footer as real text when Helvetica can say it (else ./pdf.ts draws its picture strip).
export function drawMarginText(doc: jsPDF, left: string, right: string, area: { x: number; y: number; w: number; h: number }, sizePt: number): boolean {
  if (!pdfEncodable(left) || !pdfEncodable(right)) return false;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(sizePt);
  doc.setTextColor(0x70, 0x79, 0x73);
  const baseline = area.y + area.h / 2 + sizePt * 0.35;
  const rightWidth = right ? drawnWidth(doc, right) : 0;
  let text = left;
  const room = area.w - rightWidth - sizePt * 1.2;
  while (text.length > 1 && drawnWidth(doc, text) > room) text = `${text.slice(0, -2)}…`;
  if (text) doc.text(text, area.x, baseline, { baseline: 'alphabetic' });
  // Placed by the width it is drawn at (align: 'right' measures with kerning and overshot the margin).
  if (right) doc.text(right, area.x + area.w - rightWidth, baseline, { baseline: 'alphabetic' });
  return true;
}
